import { useState, useMemo, useEffect } from "react"
import { useTableContext } from "../contexts/table-context"

export interface ColumnSettingItem {
    key: React.Key
    title: string
    dataIndex?: string | string[]
    visible: boolean
    fixed: boolean | "left" | "right"
}

export function useTableSettings<T = any>() {
    const { visibleColumns, onColumnsSelected, preferences, initialColumns } = useTableContext<T>()
    const [open, setOpen] = useState(false)

    // Initialize column settings from current columns
    const initialSettings = useMemo(() => {
        // Convert array of records to a single map
        const fixedColumnsMap = new Map<string, "left" | "right">()
        preferences?.state.fixedColumns?.forEach((record) => {
            Object.entries(record).forEach(([key, value]) => {
                fixedColumnsMap.set(key, value)
            })
        })

        return visibleColumns.map((col) => ({
            key: col.key,
            title: col.title as string,
            dataIndex: col.dataIndex,
            visible: !col.hidden,
            // Use preferences if available, otherwise use original fixed value
            fixed: fixedColumnsMap.get(col.key.toString()) || col.fixed || false
        })) as ColumnSettingItem[]
    }, [visibleColumns, preferences?.state.fixedColumns])

    const [tempSettings, setTempSettings] = useState<ColumnSettingItem[]>(initialSettings)

    // Reset temp state when modal opens
    useEffect(() => {
        if (open) {
            setTempSettings(initialSettings)
        }
    }, [open, initialSettings])

    const handleVisibilityChange = (key: React.Key, checked: boolean) => {
        setTempSettings((prev) => {
            // If unchecking, ensure at least one column remains visible
            if (!checked) {
                const visibleCount = prev.filter((item) => item.visible).length
                if (visibleCount <= 1) {
                    // This is the last visible column, don't allow unchecking
                    return prev
                }
            }
            return prev.map((item) => (item.key === key ? { ...item, visible: checked } : item))
        })
    }

    const handleFixedChange = (key: React.Key, checked: boolean) => {
        setTempSettings((prev) => prev.map((item) => (item.key === key ? { ...item, fixed: checked ? "left" : false } : item)))
    }

    const handleSelectAll = (checked: boolean) => {
        setTempSettings((prev) => {
            if (!checked) {
                // Don't allow unchecking all columns - keep at least the first one visible
                return prev.map((item, index) => ({
                    ...item,
                    visible: index === 0 ? true : false
                }))
            }
            return prev.map((item) => ({ ...item, visible: checked }))
        })
    }

    const allSelected = useMemo(() => {
        return tempSettings.every((item) => item.visible)
    }, [tempSettings])

    const someSelected = useMemo(() => {
        return tempSettings.some((item) => item.visible) && !allSelected
    }, [tempSettings, allSelected])

    const handleCancel = () => {
        setTempSettings(initialSettings)
        setOpen(false)
    }

    const handleResetToDefault = () => {
        if (preferences) {
            preferences.resetPreferences()
        }
        // Reset to all columns visible, keep original fixed columns
        const defaultSettings = tempSettings.map((item) => {
            const originalColumn = initialColumns.find((col) => col.key === item.key)
            return {
                ...item,
                visible: true,
                fixed: originalColumn?.fixed || false
            }
        })

        // Apply the reset immediately (don't wait for modal close)
        const visibleKeys = defaultSettings.filter((item) => item.visible).map((item) => item.key)
        onColumnsSelected(visibleKeys)

        setTempSettings(defaultSettings)
        // Close the modal after reset
        setOpen(false)
    }

    const handleApply = () => {
        // Apply visibility changes
        const visibleKeys = tempSettings.filter((item) => item.visible).map((item) => item.key)
        onColumnsSelected(visibleKeys)

        // Apply fixed column changes
        if (preferences) {
            const fixedColumns = tempSettings
                .filter((item) => item.fixed)
                .map((item) => ({
                    [item.key.toString()]: item.fixed as "left" | "right"
                }))
            preferences.setFixedColumns(fixedColumns)
        }

        setOpen(false)
    }

    const handleOpen = () => {
        setOpen(true)
    }

    return {
        open,
        tempSettings,
        initialColumns,
        allSelected,
        someSelected,
        handleOpen,
        handleCancel,
        handleResetToDefault,
        handleSelectAll,
        handleApply,
        handleVisibilityChange,
        handleFixedChange
    }
}
